import React from "react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { InfoIcon } from "./InfoIconTooltipHelper";

interface InfoIconTooltipProps {
  content: any[] | string;
  inlineStyles?: React.CSSProperties;
  disableTooltip?: boolean;
  props?: React.HTMLAttributes<HTMLElement>;
  handleClick?: () => void;
  role?: string;
}

/**
 * InfoIconTooltip component displays a tooltip with fee descriptions/content.
 *
 * @param {Object} props - Optional additional properties for the component.
 * @param {any[]} props.content - An array of fee items or a string description || If it is clickable, a description of the action (accessibility).
 * @param {React.CSSProperties} [props.inlineStyles] - Optional inline styles for the info icon tooltip.
 * @param {boolean} [props.disableTooltip=false] - Whether to disable the tooltip.
 * @param {() => void} [props.handleClick] - Optional click handler for the info icon.
 * @param {string} [props.role] - The role attribute for the info icon, defaults to "tooltip" if not clickable.
 * @returns {JSX.Element} The rendered component.
 */

const InfoIconTooltip: React.FC<InfoIconTooltipProps> = ({
  content,
  inlineStyles,
  disableTooltip = false,
  handleClick,
  role = "tooltip",
  ...props
}: InfoIconTooltipProps) => {
  const isContentAnArray =
    content && content?.length > 0 && Array.isArray(content);
  let contentDescription = isContentAnArray ? "" : content;

  isContentAnArray &&
    content.map((feeItem: any, i: number) => {
      contentDescription += feeItem.description;
      if (i > 0) {
        contentDescription += "<br /><br />";
      }
    });

  const isClickable = handleClick !== undefined;
  return (
    <Tippy
      content={<div dangerouslySetInnerHTML={{ __html: contentDescription }} />}
      theme="bootstrap"
      disabled={disableTooltip}
    >
      <span
        {...props}
        className={`d-inline-flex align-items-center`}
        style={{
          height: "20px",
          cursor: `${isClickable ? "pointer" : "default"}`,
          ...inlineStyles,
        }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (isClickable && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleClick();
          }
        }}
        role={isClickable ? "button" : role}
        aria-label={contentDescription}
        onClick={handleClick}
      >
        <InfoIcon />
      </span>
    </Tippy>
  );
};

export default InfoIconTooltip;
